import api from './axios';

// ─── API calls (all routed through existing axios with Bearer token) ───────
export const dashApi = {
  // CEO Dashboard
  getEcmsPoolTop5:   (year) => api.post('/api/dash/enterprise/getTop5EntpLoadingThisYear', { year }),
  getEcmsPfTop5:     (year) => api.post('/api/dash/enterprise/getTop5EntpOnPlatformFee',   { year }),
  getFleetPoolTop5:  (year) => api.post('/api/dash/fleet/getTop5FleetLoadingThisYear',      { year }),
  getEcmsPoolTrend:  ()     => api.post('/api/dash/common/poolAccountLoading/lastYearToTillDate'),
  getEcmsPfTrend:    ()     => api.post('/api/dash/common/platformfee/lastYearToTillDate'),
  getFleetPoolTrend: ()     => api.post('/api/dash/fleet/poolAccountLoading/lastYearToTillDate'),

  // New dashboard stats
  getLiveStats:   ()          => api.post('/api/dash/common/liveStats'),
  getMonthStats:  (monthStr)  => api.post('/api/dash/common/monthStats', { monthStr }),

  // Drill-down stats
  getPoolDayStats:  (monthStr) => api.post('/api/dash/common/poolDayStats',  { monthStr }),
  getPoolHourStats: (dayStr)   => api.post('/api/dash/common/poolHourStats', { dayStr }),
  getPfDayStats:    (monthStr) => api.post('/api/dash/common/pfDayStats',    { monthStr }),
  getPfHourStats:   (dayStr)   => api.post('/api/dash/common/pfHourStats',   { dayStr }),

  // Enterprise list pages
  getEcmsEnterprises:  ()   => api.post('/api/dash/enterprise'),
  getFleetEnterprises: ()   => api.post('/api/dash/fleet'),

  // Enterprise modal tabs
  getEcmsMembers:       (id) => api.post('/api/dash/enterprise/members',            { enterpriseId: id }),
  getEcmsMemberSummary: (id) => api.post('/api/dash/enterprise/memberSummary',      { enterpriseId: id }),
  getEcmsUserSummary:   (id) => api.post('/api/dash/enterprise/userSummary',        { enterpriseId: id }),
  getEcmsFunctionality: (id)=> api.post('/api/dash/enterprise/functionality',       { enterpriseId: id }),
  getEcmsFuncTypes:  ()     => api.post('/api/dash/enterprise/functionalityWithDataType'),
  updateEcmsFunctionality: (body) => api.post('/api/dash/enterprise/updateEntpFunctionality', body),
  getEcmsVirtualAccounts: (id) => api.post('/api/dash/enterprise/virtualAccountNumberData', { enterpriseId: id }),
  getEcmsPoolLoading: (id)  => api.post('/api/dash/enterprise/getPoolAccountLoadingData',   { enterpriseId: id }),
  getEcmsPlatformFee: (id)  => api.post('/api/dash/enterprise/getPlatformFeeData',          { enterpriseId: id }),

  // iFleet modal tabs (mirrors enterprise but under /fleet)
  getFleetMembers:       (id) => api.post('/api/dash/fleet/members',                { enterpriseId: id }),
  getFleetMemberSummary: (id) => api.post('/api/dash/fleet/memberSummary',          { enterpriseId: id }),
  getFleetUserSummary:   (id) => api.post('/api/dash/fleet/userSummary',            { enterpriseId: id }),
  getFleetFunctionality: (id)=> api.post('/api/dash/fleet/functionality',           { enterpriseId: id }),
  getFleetFuncTypes:  ()    => api.post('/api/dash/fleet/functionalityWithDataType'),
  updateFleetFunctionality: (body) => api.post('/api/dash/fleet/updateEntpFunctionality', body),
  getFleetVirtualAccounts: (id) => api.post('/api/dash/fleet/virtualAccountNumberData', { enterpriseId: id }),
  getFleetPoolLoading: (id) => api.post('/api/dash/fleet/getPoolAccountLoadingData',    { enterpriseId: id }),
  getFleetPlatformFee: (id) => api.post('/api/dash/fleet/getPlatformFeeData',           { enterpriseId: id }),

  // Analyze — numeric months (1-12)
  analyzeMonthRange: (currMonth, prevMonth, currYear, prevYear) =>
    api.post('/api/dash/analyze/monthRange', {
      currMonth: Number(currMonth),
      prevMonth: Number(prevMonth),
      currYear:  Number(currYear),
      prevYear:  Number(prevYear),
    }),

  // Downloads (blob)
  downloadEcmsTop5:  (year) => api.post('/api/dash/download/ecms/top5',  { year }, { responseType: 'blob' }),
  downloadFleetTop5: (year) => api.post('/api/dash/download/fleet/top5', { year }, { responseType: 'blob' }),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

export const YEAR_OPTIONS = [new Date().getFullYear(), new Date().getFullYear() - 1];

export function getCurrentMonthName() {
  return MONTH_NAMES[new Date().getMonth()];
}

export function getPrevMonthName(monthName) {
  const idx = MONTH_NAMES.indexOf(monthName);
  return MONTH_NAMES[(idx + 11) % 12];
}

export function formatAmount(value) {
  if (value == null) return '—';
  if (value >= 1e7) return '₹ ' + (value / 1e7).toFixed(2) + ' Cr';
  if (value >= 1e5) return '₹ ' + (value / 1e5).toFixed(2) + ' L';
  if (value >= 1e3) return '₹ ' + (value / 1e3).toFixed(2) + ' K';
  return '₹ ' + value.toLocaleString();
}

export function toCr(value) {
  if (value == null) return null;
  return parseFloat((value / 1e7).toFixed(2));
}

export function calcMoM(current, prev) {
  if (!prev || prev === 0) return null;
  return parseFloat(((current - prev) / prev * 100).toFixed(1));
}

/** Convert { "2025": { "May": 220000000 } } → recharts-friendly array sorted by time */
export function formatTrendData(apiData) {
  if (!apiData) return [];
  const result = [];
  Object.entries(apiData).forEach(([year, months]) => {
    if (!/^\d{4}$/.test(year)) return; // skip non-year keys like cachedAt
    Object.entries(months).forEach(([month, amount]) => {
      result.push({
        label:    `${month.slice(0,3)} '${String(year).slice(2)}`,
        month,
        year:     parseInt(year),
        amount:   parseFloat(amount),
        amountCr: parseFloat((parseFloat(amount) / 1e7).toFixed(2)),
        sortKey:  parseInt(year) * 100 + MONTH_NAMES.indexOf(month),
      });
    });
  });
  return result.sort((a, b) => a.sortKey - b.sortKey);
}

/** Merge ECMS + Fleet trend arrays keyed by sortKey */
export function mergeTrendArrays(ecmsArr, fleetArr) {
  const fleetMap = new Map((fleetArr || []).map(d => [d.sortKey, d.amountCr]));
  return (ecmsArr || []).map(d => {
    const monthNum = String(MONTH_NAMES.indexOf(d.month) + 1).padStart(2, '0');
    const monthStr = `${d.year}-${monthNum}`;
    return {
      label:    d.label,
      month:    d.month,
      year:     d.year,
      monthStr,                                        // e.g. '2026-05' for drill-down
      ecms:     d.amountCr,
      fleet:    fleetMap.get(d.sortKey) ?? 0,
      combined: d.amountCr + (fleetMap.get(d.sortKey) ?? 0),
      sortKey:  d.sortKey,
    };
  });
}

/** Find peak & low objects in a trend array by a numeric key */
export function peakAndLow(arr, key) {
  if (!arr || arr.length === 0) return { peak: null, low: null };
  const peak = arr.reduce((m, d) => d[key] > m[key] ? d : m, arr[0]);
  const low  = arr.reduce((m, d) => d[key] < m[key] ? d : m, arr[0]);
  return { peak, low };
}

export function triggerDownload(blobData, filename) {
  const url = window.URL.createObjectURL(new Blob([blobData]));
  const a   = document.createElement('a');
  a.href    = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}
